import { isUndefined } from 'angular';
import mustache from 'mustache';
import { defaults, snakeCase } from 'lodash-es';
import { actions, selectors } from '../../state';
import {
  moveDateChangeNotification as MOVE_DATE_COPY_ENTRY_ID,
  updaterMoveDateChangeNotification as UPDATER_MOVE_DATE_COPY_ENTRY_ID,
} from '../../state/contentful/entries';
import { QUERIES } from '../common/directives/match-media';
import { getVariation } from '@updater-core/lib/optimizely';

import { itemStatusesByKind, statusFromItems } from './dashboard.utils';

import { ITEM_STATUSES } from '../../constants/items';
import { ITEM_DEFINITION_CODES } from '../../constants/item-definitions';
import { STATES } from '../../constants/states';
import template from './dashboard.html';
import './dashboard.global.css';
import { ENTITY_NAMES } from '../../constants';
import createMoveYourStuffInteractor from '@updater-apps/move-your-stuff/interactors/mys';
import createFullServiceMoveInteractor from '@updater-apps/full-service-move/interactors/fsm';
import createPackingSuppliesInteractor from '@updater-apps/packing-supplies/interactors';

const defaultMessage = `This date change will ONLY affect your Updater Account.
Please contact {{name}} directly if you need to change your actual move date.`;

const defaultUpdaterMessage = `This date change will ONLY affect your Updater Account.`;

const getMoveDateSuccessMessage = (
  name,
  { message = defaultMessage, updaterMessage = defaultUpdaterMessage }
) =>
  name && name !== ENTITY_NAMES.UPDATER
    ? mustache.render(message, { name })
    : mustache.render(updaterMessage, { name });

const MOVE_DATE_ERROR =
  'Oops! Looks like there was an issue changing your move date.';

const experimentName = 'Automate_Your_Home_Step';

class DashboardController {
  constructor($timeout, updStore, $scope, $stateParams) {
    'ngInject';
    Object.assign(this, { $timeout, updStore, $scope, $stateParams });
  }

  $onInit() {
    this.$scope.$on(
      '$stateChangeStart',
      () => (DashboardController.scrollPosition = window.pageYOffset)
    );
    // moveId is only provided when the user has switched to a new move
    if (this.$stateParams.moveId) {
      // In this case, don't keep the dashboard state from other moves
      Object.assign(DashboardController, {
        expanded: undefined,
        scrollPosition: 0,
      });
      window.scrollTo(0, 0);
    } else {
      this._expanded = DashboardController.expanded;
      this.$timeout(() =>
        window.scrollTo(0, DashboardController.scrollPosition || 0)
      );
    }

    getVariation(experimentName).then((variation) => {
      this.smartHomeVariation = variation;
    });

    const moveYourStuffInteractor = createMoveYourStuffInteractor();
    moveYourStuffInteractor.initialized().then(() => {
      const { eligible } = moveYourStuffInteractor.getState();
      this.shouldSeeNewMoveYourStuffStep = eligible;
    });

    const fullServiceMoveInteractor = createFullServiceMoveInteractor();
    fullServiceMoveInteractor.experimentInitialized().then(() => {
      const { eligible } = fullServiceMoveInteractor.getState();
      this.relocationSpecialistsEnabled = eligible;
    });

    const packingSuppliesInteractor = createPackingSuppliesInteractor();
    packingSuppliesInteractor.experimentInitialized().then(() => {
      const { variation } = packingSuppliesInteractor.getState();
      this.packingSuppliesEnabled = variation && variation !== 'control';
    });

    this.disconnect = this.updStore.connect(mapStateToThis.bind(this))(this);
    this.updStore.dispatch(
      actions.contentful.contentfulGet(MOVE_DATE_COPY_ENTRY_ID)
    );
    this.updStore.dispatch(
      actions.contentful.contentfulGet(UPDATER_MOVE_DATE_COPY_ENTRY_ID)
    );
  }

  $onDestroy() {
    this.disconnect();
  }

  get headerHeight() {
    return window.matchMedia(QUERIES.xs).match ? 60 : 80;
  }

  get verifySref() {
    return STATES.VERIFY;
  }

  set expanded(value) {
    this._expanded = value;
    this.phases = this.phases.map((phase) => ({
      ...phase,
      expanded: value.indexOf(phase.id) !== -1,
    }));
    DashboardController.expanded = value;
  }

  expandAll() {
    this.expanded = this.phases.map((phase) => phase.id);
  }

  collapseAll() {
    this.expanded = [];
  }

  updateMoveDate(date) {
    const { dispatch } = this.updStore;

    dispatch(actions.move.singleton(this.move.id).update({ date })).then(
      () => {
        dispatch(actions.message.success(this.moveDateSuccessMessage));
        dispatch(actions.analytics.track('Saved Move Date'));
      },
      () => dispatch(actions.message.error(MOVE_DATE_ERROR))
    );
  }

  handleItemStatusChange({ code, status, uiState }) {
    const state = this.updStore.getState();
    const dispatchQueue = [];

    switch (status) {
      case ITEM_STATUSES.STARTED:
        {
          const move = selectors.current.move(state);
          const declinedStatus = move.declinedStatusByCode(code, state);

          if (declinedStatus === ITEM_STATUSES.NOT_NEEDED) {
            dispatchQueue.push(
              actions.move.undecline(this.move.id, code, declinedStatus)
            );
          }

          dispatchQueue.push(
            actions.router.stateGo(uiState.name, uiState.params)
          );
        }
        break;

      case ITEM_STATUSES.ALREADY_DONE:
      case ITEM_STATUSES.NOT_NEEDED:
        dispatchQueue.push(actions.move.decline(this.move.id, code, status));
        break;
    }

    dispatchQueue.forEach(this.dispatch);
  }
}

function mapStateToThis(state) {
  const user = selectors.current.user(state);
  const move = selectors.current.move(state);
  const siteConfig = selectors.current.siteConfig();

  const itemDefinitions = selectors.itemDefinition
    .all(state)
    .map((itemDefinition) => ({
      id: itemDefinition.id(state),
      code: itemDefinition.code(state),
      completedDescription: itemDefinition.completedDescription(state),
      icon: itemDefinition.icon(state),
      longDescription: itemDefinition.longDescription(state),
      order: itemDefinition.order(state),
      phaseId: itemDefinition.phaseId(state).toString(),
      required: (itemDefinition.page(state) || {}).required,
      shortDescription: itemDefinition.shortDescription(state),
      uiState: itemDefinition.uiState(state),
    }));

  const itemStatuses = itemStatusesByKind(state);

  // true & hasMoveYourStuff => pull out MoveIQ
  // false = pull out MYS
  const shouldSeeNewMoveYourStuffStep = this.shouldSeeNewMoveYourStuffStep;
  const relocationSpecialistsEnabled = this.relocationSpecialistsEnabled;
  const packingSuppliesEnabled = this.packingSuppliesEnabled;

  const phases = selectors.itemDefinitionPhase
    .all(state)
    .map((phase, _) => {
      const name = phase.name(state);
      const nameSnakeCased = snakeCase(name);
      const id = phase.id(state);
      // Check if MOVE_YOUR_STUFF is in the itemDefinitions array
      const hasMoveYourStuff =
        itemDefinitions.filter(
          (step) => step.code === ITEM_DEFINITION_CODES.MOVE_YOUR_STUFF
        ).length > 0;

      const hasRelocationSpecialists =
        itemDefinitions.filter(
          (step) => step.code === ITEM_DEFINITION_CODES.RELOCATION_SPECIALISTS
        ).length > 0;
      const shouldSeeRelocationSpecialistsStep =
        relocationSpecialistsEnabled && hasRelocationSpecialists;

      return {
        tableId: id,
        expanded:
          isUndefined(this._expanded) ||
          this._expanded.indexOf(nameSnakeCased) !== -1,
        id: nameSnakeCased,
        name,
        order: phase.order(state),
        steps: itemDefinitions
          .map((itemDefinition) =>
            defaults(
              {
                itemStatuses: itemStatuses[itemDefinition.code] || [],
              },
              itemDefinition,
              // Determine status by order of importance:
              // 1. If the item-definition code exists within the "declined items" array.
              // 2. If the item-definition has corresponding move item(s.
              // 3. If none of the above, default to "not started".
              { status: move.declinedStatusByCode(itemDefinition.code, state) },
              statusFromItems(itemStatuses[itemDefinition.code]),
              { status: ITEM_STATUSES.NOT_STARTED }
            )
          )
          .filter((step) => step.phaseId === id)
          .filter(
            (step) =>
              this.smartHomeVariation === 'variant' ||
              step.code !== ITEM_DEFINITION_CODES['SMART_HOME_OWN']
          )
          // Pull out MoveIQ if (shouldSeeNewMoveYourStuffStep = true & hasMoveYourStuff) or shouldSeeRelocationSpecialistsStep = true
          .filter(
            (step) =>
              !(
                step.code === ITEM_DEFINITION_CODES.MOVEIQ &&
                ((shouldSeeNewMoveYourStuffStep && hasMoveYourStuff) ||
                  shouldSeeRelocationSpecialistsStep)
              )
          )
          // Pull out MYS if shouldSeeNewMoveYourStuffStep = false or shouldSeeRelocationSpecialistsStep = true
          .filter(
            (step) =>
              !(
                step.code === ITEM_DEFINITION_CODES.MOVE_YOUR_STUFF &&
                (!shouldSeeNewMoveYourStuffStep ||
                  shouldSeeRelocationSpecialistsStep)
              )
          )
          // Pull out FSM if shouldSeeFullServiceMoveStep = false
          .filter(
            (step) =>
              !(
                step.code === ITEM_DEFINITION_CODES.RELOCATION_SPECIALISTS &&
                !shouldSeeRelocationSpecialistsStep
              )
          )
          // Pull out Packing Supplies if shouldSeeFullServiceMoveStep = false
          .filter(
            (step) =>
              !(
                step.code === ITEM_DEFINITION_CODES.PACKING_SUPPLIES &&
                !packingSuppliesEnabled
              )
          )
          .filter((step) => !!step.uiState.name)
          .sort(sortByOrder),
        subHeaderName: phase.subHeaderName(state),
        analytics: {
          event: 'Clicked Phase Accordion',
          properties: {
            'Phase ID': id,
            'Phase Name': name,
          },
        },
      };
    })
    .filter((phase) => phase.steps.length > 0)
    .sort(sortByOrder);

  return {
    loadingMove: selectors.pending.match(
      (action) =>
        actions.siteConfig.isRead(action) || actions.user.isUpdate(action),
      state
    ),
    locationHeroImageId: 'static_dashboard_hero_image.jpg',
    move: {
      id: move.id(state),
      date: move.date(state),
      toAddress: move.toAddress(state),
      fromAddress: move.fromAddress(state),
      needToVerify: move.needToVerify(state),
    },
    phases,
    showHero:
      selectors.router.currentParamsProp('showHero', state) ||
      selectors.router.currentDataProp('showHero', state),
    user: {
      firstName: user.firstName(state),
      lastName: user.lastName(state),
      signedUpAt: user.signedUpAt(state),
    },
    moveDateSuccessMessage: getMoveDateSuccessMessage(
      siteConfig.divisionName(state) || siteConfig.companyName(state),
      {
        message: selectors.contentful.getEntry(state, MOVE_DATE_COPY_ENTRY_ID)
          ?.fields?.value,
        updaterMessage: selectors.contentful.getEntry(
          state,
          UPDATER_MOVE_DATE_COPY_ENTRY_ID
        )?.fields?.value,
      }
    ),
  };
}

function sortByOrder(a, b) {
  return a.order > b.order ? 1 : -1;
}

export const DashboardComponent = {
  controller: DashboardController,
  template,
};
